import { useCallback, useEffect, useRef, useState } from 'react';

export type NetworkStatus = 'online' | 'offline' | 'unknown';
export type NetworkQuality = 'good' | 'fair' | 'degraded' | 'offline' | 'unknown';

export interface BrowserConnectionInfo {
  effectiveType?: string;
  downlinkMbps?: number;
  rttMs?: number;
  saveData?: boolean;
}

export interface NetworkSample {
  timestamp: number;
  success: boolean;
  latencyMs: number | null;
}

export interface NetworkState {
  status: NetworkStatus;
  isOnline: boolean;
  isOffline: boolean;
  browserOnline: boolean;
  apiReachable: boolean;
  latencyMs: number | null;
  jitterMs: number | null;
  failureRate: number;
  consecutiveFailures: number;
  quality: NetworkQuality;
  lastCheckedAt: Date | null;
  lastSuccessfulAt: Date | null;
  lastChangedAt: Date | null;
  wasOffline: boolean;
  browserConnection: BrowserConnectionInfo;
}

export interface UseNetworkStatusReturn extends NetworkState {
  checkConnection: () => Promise<boolean>;
}

interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
    addEventListener?: (type: 'change', listener: EventListener) => void;
    removeEventListener?: (type: 'change', listener: EventListener) => void;
  };
  mozConnection?: NavigatorWithConnection['connection'];
  webkitConnection?: NavigatorWithConnection['connection'];
}

const CHECK_INTERVAL_MS = 10_000;
const CHECK_TIMEOUT_MS = 5_000;
const SAMPLE_WINDOW_SIZE = 20;
const OFFLINE_FAILURE_THRESHOLD = 2;

const getBrowserConnection = (): BrowserConnectionInfo => {
  if (typeof navigator === 'undefined') return {};
  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
  if (!connection) return {};
  return {
    effectiveType: connection.effectiveType,
    downlinkMbps: connection.downlink,
    rttMs: connection.rtt,
    saveData: connection.saveData,
  };
};

export const calculateJitter = (samples: NetworkSample[]): number | null => {
  const successfulLatencies = samples
    .filter((sample) => sample.success && sample.latencyMs !== null)
    .map((sample) => sample.latencyMs as number);

  if (successfulLatencies.length < 2) return null;

  const deltas = successfulLatencies.slice(1).map((latency, index) =>
    Math.abs(latency - successfulLatencies[index])
  );

  return Math.round(deltas.reduce((sum, delta) => sum + delta, 0) / deltas.length);
};

export const calculateFailureRate = (samples: NetworkSample[]): number => {
  if (!samples.length) return 0;
  const failures = samples.filter((sample) => !sample.success).length;
  return Math.round((failures / samples.length) * 100);
};

export const getNetworkQuality = ({
  isOffline,
  latencyMs,
  jitterMs,
  failureRate,
}: {
  isOffline: boolean;
  latencyMs: number | null;
  jitterMs: number | null;
  failureRate: number;
}): NetworkQuality => {
  if (isOffline) return 'offline';
  if (latencyMs === null) return 'unknown';
  if (latencyMs > 400 || (jitterMs ?? 0) > 200 || failureRate >= 15) return 'degraded';
  if (latencyMs > 150 || (jitterMs ?? 0) > 80 || failureRate >= 5) return 'fair';
  return 'good';
};

const buildHealthUrl = (): string => {
  const base = (import.meta.env.VITE_NETSUITE_HOST_API || '').replace(/\/$/, '');
  return `${base}/health`;
};

/**
 * Monitors actual API reachability instead of relying only on navigator.onLine.
 * A single failed probe is treated as transient; two consecutive API failures
 * are required before the POS is considered offline.
 */
export function useNetworkStatus(): UseNetworkStatusReturn {
  const initialBrowserOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const [state, setState] = useState<NetworkState>({
    status: initialBrowserOnline ? 'unknown' : 'offline',
    isOnline: initialBrowserOnline,
    isOffline: !initialBrowserOnline,
    browserOnline: initialBrowserOnline,
    apiReachable: false,
    latencyMs: null,
    jitterMs: null,
    failureRate: 0,
    consecutiveFailures: 0,
    quality: initialBrowserOnline ? 'unknown' : 'offline',
    lastCheckedAt: null,
    lastSuccessfulAt: null,
    lastChangedAt: null,
    wasOffline: false,
    browserConnection: getBrowserConnection(),
  });

  const samplesRef = useRef<NetworkSample[]>([]);
  const consecutiveFailuresRef = useRef(0);
  const currentOfflineRef = useRef(!initialBrowserOnline);

  const applyBrowserOffline = useCallback(() => {
    currentOfflineRef.current = true;
    consecutiveFailuresRef.current = Math.max(
      consecutiveFailuresRef.current,
      OFFLINE_FAILURE_THRESHOLD
    );
    setState((prev) => ({
      ...prev,
      status: 'offline',
      isOnline: false,
      isOffline: true,
      browserOnline: false,
      apiReachable: false,
      consecutiveFailures: consecutiveFailuresRef.current,
      quality: 'offline',
      lastChangedAt: prev.isOffline ? prev.lastChangedAt : new Date(),
      wasOffline: false,
      browserConnection: getBrowserConnection(),
    }));
  }, []);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    const browserOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
    const startedAt = performance.now();
    let success = false;
    let latencyMs: number | null = null;

    if (browserOnline) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
      try {
        const response = await fetch(buildHealthUrl(), {
          method: 'GET',
          cache: 'no-store',
          credentials: 'omit',
          signal: controller.signal,
        });
        success = response.ok;
        latencyMs = Math.round(performance.now() - startedAt);
      } catch {
        success = false;
        latencyMs = null;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    if (success) consecutiveFailuresRef.current = 0;
    else consecutiveFailuresRef.current += 1;

    const sample: NetworkSample = {
      timestamp: Date.now(),
      success,
      latencyMs,
    };
    samplesRef.current = [...samplesRef.current, sample].slice(-SAMPLE_WINDOW_SIZE);

    const jitterMs = calculateJitter(samplesRef.current);
    const failureRate = calculateFailureRate(samplesRef.current);
    const shouldBeOffline =
      !browserOnline || consecutiveFailuresRef.current >= OFFLINE_FAILURE_THRESHOLD;
    const wasOffline = currentOfflineRef.current && !shouldBeOffline && success;
    const status: NetworkStatus = shouldBeOffline ? 'offline' : success ? 'online' : 'unknown';
    const quality = getNetworkQuality({
      isOffline: shouldBeOffline,
      latencyMs,
      jitterMs,
      failureRate,
    });

    currentOfflineRef.current = shouldBeOffline;

    setState((prev) => ({
      status,
      isOnline: !shouldBeOffline,
      isOffline: shouldBeOffline,
      browserOnline,
      apiReachable: success,
      latencyMs,
      jitterMs,
      failureRate,
      consecutiveFailures: consecutiveFailuresRef.current,
      quality,
      lastCheckedAt: new Date(sample.timestamp),
      lastSuccessfulAt: success ? new Date(sample.timestamp) : prev.lastSuccessfulAt,
      lastChangedAt:
        prev.isOffline !== shouldBeOffline || (success && prev.status !== 'online')
          ? new Date(sample.timestamp)
          : prev.lastChangedAt,
      wasOffline,
      browserConnection: getBrowserConnection(),
    }));

    return success;
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setState((prev) => ({
        ...prev,
        browserOnline: true,
        browserConnection: getBrowserConnection(),
      }));
      void checkConnection();
    };

    const handleOffline = () => applyBrowserOffline();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const nav = navigator as NavigatorWithConnection;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    const handleConnectionChange: EventListener = () => {
      setState((prev) => ({
        ...prev,
        browserConnection: getBrowserConnection(),
      }));
    };
    connection?.addEventListener?.('change', handleConnectionChange);

    void checkConnection();
    const interval = window.setInterval(() => void checkConnection(), CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      connection?.removeEventListener?.('change', handleConnectionChange);
      window.clearInterval(interval);
    };
  }, [applyBrowserOffline, checkConnection]);

  return {
    ...state,
    checkConnection,
  };
}

export default useNetworkStatus;
